import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { OVERVIEW_CONTACT_MOLD_PARAMETER_ID } from '@ivario-arc/shared/src/sampleResults/mold.ts';
import { MoldConcentration } from '@ivario-arc/shared/src/enums.ts';
import { SampleDetailsDto } from '@src/types/types.ts';
import { ParameterDetailPopup } from '@components/atoms/ParameterDetailPopup.tsx';
import { StyledMarkdown } from '@components/atoms/StyledMarkdown.tsx';
import { ActiveLink } from '@components/atoms/ActiveLink.tsx';
import { RegulationsUpselling } from '@components/sample-details/RegulationsUpselling.tsx';
import { Card } from '@components/atoms/Card.tsx';
import StatusMessage from '@components/sample-details/ParametersStatusMessage.tsx';
import { ContactMoldParameter } from '@components/sample-details/mold/ContactMoldParameter.tsx';
import { ErrorContent } from '@components/atoms/ErrorContent.tsx';
import {
  CONTACT_MOLD_KIND_PRIORITY,
  getContactMoldConcentrationFromValue,
  getContactMoldKindFromValue,
  getContactMoldOverviewConcentration,
  getContactMoldOverviewStrength,
  MOLD_CONCENTRATION_PRIORITY,
} from '@utils/parameters.ts';
import { ParameterStatusType } from '@components/atoms/ParameterStatusType.ts';

export const ContactMoldResults = ({
  sample,
}: {
  sample: SampleDetailsDto;
}) => {
  const { t } = useTranslation('component', {
    keyPrefix: 'parameterList',
  });
  const { t: tError } = useTranslation('component', {
    keyPrefix: 'parameterList.moldAnalysisError',
  });
  const [showDetail, setShowDetail] = useState(false);

  const overviewParameter = sample.parameters.find(
    (parameter) => parameter.pmId === OVERVIEW_CONTACT_MOLD_PARAMETER_ID,
  );

  if (!overviewParameter) {
    return (
      <ErrorContent
        fullScreen={false}
        backToProfile={tError('backToProfile')}
        title={tError('title')}
        description={t('errors.noMoldDataAvailable')}
      />
    );
  }

  // Filter out the parameter with ID "Insekten oder Milben Befall"
  const otherParameters = sample.parameters
    .filter(
      (parameter) =>
        parameter.pmId !== OVERVIEW_CONTACT_MOLD_PARAMETER_ID &&
        !parameter.name?.includes('Insekten oder Milben Befall'),
    )
    .sort((a, b) => {
      // Get kinds for both parameters - primary sorting criteria
      const kindA = getContactMoldKindFromValue(a.value);
      const kindB = getContactMoldKindFromValue(b.value);

      // First, compare by kind (Befall > Anflug > none)
      if (kindA !== kindB) {
        return (
          CONTACT_MOLD_KIND_PRIORITY[kindB] - CONTACT_MOLD_KIND_PRIORITY[kindA]
        );
      }

      // If same kind, compare by concentration (High > Medium > Low > No)
      const concentrationA = getContactMoldConcentrationFromValue(a.value);
      const concentrationB = getContactMoldConcentrationFromValue(b.value);

      if (concentrationA !== concentrationB) {
        return (
          MOLD_CONCENTRATION_PRIORITY[concentrationB] -
          MOLD_CONCENTRATION_PRIORITY[concentrationA]
        );
      }

      // If both have same kind and concentration, sort alphabetically by name
      return (a.name ?? '').localeCompare(b.name ?? '');
    });

  const overviewStrength = getContactMoldOverviewStrength(
    overviewParameter.value,
  );
  const concentration = getContactMoldOverviewConcentration(overviewParameter);

  // Find the matching regulation for displaying explanations
  const activeRegulation = overviewParameter.regulations.find((regulation) => {
    return regulation.contactMoldOverviewStrength === overviewStrength;
  });

  // Get status type (success, warning, error) based on concentration
  let statusType: ParameterStatusType = ParameterStatusType.SUCCESS;
  if (concentration === MoldConcentration.MEDIUM_MOLD) {
    statusType = ParameterStatusType.WARNING;
  } else if (concentration === MoldConcentration.HIGH_MOLD) {
    statusType = ParameterStatusType.ERROR;
  }

  // Prepare the status message title
  const statusTitle =
    concentration === MoldConcentration.NO_MOLD
      ? t('keinBefund')
      : t(`moldValues.${concentration}`);

  return (
    <div className="space-y-6">
      <StatusMessage
        type={statusType}
        title={statusTitle}
        description={t(`moldShortInfo.${concentration}`)}
        onInfoTap={() => {
          setShowDetail(true);
        }}
        labels={[
          {
            isActive: concentration === MoldConcentration.NO_MOLD,
            text: t(`moldValues.${MoldConcentration.NO_MOLD}`),
          },
          {
            isActive: concentration === MoldConcentration.LOW_MOLD,
            text: t(`moldValues.${MoldConcentration.LOW_MOLD}`),
          },
          {
            isActive: concentration === MoldConcentration.MEDIUM_MOLD,
            text: t(`moldValues.${MoldConcentration.MEDIUM_MOLD}`),
          },
          {
            isActive: concentration === MoldConcentration.HIGH_MOLD,
            text: t(`moldValues.${MoldConcentration.HIGH_MOLD}`),
          },
        ]}
      />
      {activeRegulation && (
        <StyledMarkdown markdown={activeRegulation?.explanation} />
      )}
      <ActiveLink onTap={() => setShowDetail(true)} isActive={true}>
        <span className="text-ivarioBlue">{t('moreInfoOnParam')}</span>
      </ActiveLink>
      {(activeRegulation?.upselling?.length ?? 0) > 0 && (
        <Card>
          <RegulationsUpselling upsellingItems={activeRegulation?.upselling} />
        </Card>
      )}
      {showDetail && overviewParameter && (
        <ParameterDetailPopup
          parameter={overviewParameter}
          onClose={() => setShowDetail(false)}
        />
      )}
      {otherParameters?.map((p) => (
        <ContactMoldParameter key={p.id} parameter={p} />
      ))}
    </div>
  );
};
